import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import style from "./InstructorGrid.module.css";
import { deriveInstructors } from "@/features/landing/deriveInstructors";
import { useCurrentLanguage } from "@/i18n/useLocalized";

// Four, not five: the portrait wants a 4:5 frame to hold its proportions, and
// a fifth column squeezed every face into a slot too narrow to read.
const COUNT = 4;

function initials(name) {
	return (name ?? "")
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");
}

function InstructorGrid({ courses }) {
	const { t } = useTranslation();
	const language = useCurrentLanguage();
	const instructors = useMemo(
		() => deriveInstructors(courses, COUNT, undefined, language),
		[courses, language]
	);

	if (instructors.length === 0) return null;

	return (
		<section className={style.section}>
			<div className={style.inner}>
				<div className={style.head}>
					<h2 className={style.title}>{t("landing:lecturers.title")}</h2>
					<p className={style.sub}>{t("landing:lecturers.sub")}</p>
				</div>

				<div className={style.grid}>
					{instructors.map((instructor, index) => (
						<article
							key={`${instructor.name}-${index}`}
							className={style.card}
							style={{ "--focus": instructor.focus ?? "50% 50%" }}
						>
							{instructor.pictureUrl ? (
								<>
									<img
										className={style.photo}
										src={instructor.pictureUrl}
										// The name is set in text directly
										// below; announcing it twice is noise.
										alt=""
										loading="lazy"
									/>
									<div
										className={style.tint}
										aria-hidden="true"
									></div>
								</>
							) : (
								<div
									className={style.fallback}
									aria-hidden="true"
								>
									{initials(instructor.name)}
								</div>
							)}

							<div className={style.scrim} aria-hidden="true"></div>

							<div className={style.meta}>
								<h3 className={style.name}>{instructor.name}</h3>
								{/* The categories are the fallback, not the
								    intent: a role says what someone does, a
								    category list says where their courses are
								    filed. */}
								<p className={style.role}>
									{instructor.role
										? t(instructor.role)
										: instructor.field}
								</p>
								{/* A lecturer who teaches across two disciplines
								    gets a chip for each. Nothing renders for one
								    whose courses carry no root category, rather
								    than an empty chip. */}
								{instructor.categories.length > 0 && (
									<ul className={style.tags}>
										{instructor.categories.map((category) => (
											<li
												key={category}
												className={style.tag}
											>
												{category}
											</li>
										))}
									</ul>
								)}
							</div>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}

export default InstructorGrid;
